import type { SessionProjection } from "../domain";
import { formatAge } from "../lib/time";

interface StatusClusterProps {
  session: SessionProjection;
  compact?: boolean;
}

export function StatusCluster({ session, compact = false }: StatusClusterProps) {
  const gatewayTone = session.gateway.state === "connected" ? "live" : session.gateway.state === "reconnecting" ? "attention" : "fault";
  const hostTone = session.host.state === "reachable" ? "live" : "fault";
  const projectionTone = session.projection.state === "live" ? "live" : "attention";

  return (
    <div
      className={`status-cluster${compact ? " status-cluster--compact" : ""}`}
      aria-label={`Connection status: gateway ${session.gateway.state}; authoritative Forge host ${session.host.state}; projection ${session.projection.state}, ${formatAge(session.projection.sourceEventAt)} old`}
    >
      <span className="status-pill status-pill--gateway" data-tone={gatewayTone} title="Gateway connection">
        <span aria-hidden="true" className="status-dot" />
        <span className="status-label">Gateway</span> {session.gateway.state}
      </span>
      <span className="status-pill status-pill--host" data-tone={hostTone} title="Authoritative Forge host">
        <span aria-hidden="true" className="status-dot" />
        <span className="status-label">Host</span> {session.host.state}
      </span>
      <span className="status-pill status-pill--projection" data-tone={projectionTone} title="Age of authoritative Crew projection">
        <span aria-hidden="true" className="status-dot" />
        <span className="status-label">Data</span> {session.projection.state} · {formatAge(session.projection.sourceEventAt)}
      </span>
    </div>
  );
}

export function ProjectionBanner({ session }: { session: SessionProjection }) {
  if (session.gateway.state === "connected" && session.host.state === "reachable" && session.projection.state === "live") return null;

  let tone = "attention";
  let copy = `Showing a ${formatAge(session.projection.snapshotAt)}-old trusted snapshot. Mutations are disabled until fresh data returns.`;
  if (session.host.state === "unavailable") {
    tone = "fault";
    copy = `The Forge host is unavailable. This is the last trusted projection; this phone cannot continue Crew execution.`;
  } else if (session.gateway.state === "reconnecting") {
    copy = `Reconnecting to the gateway. Read-only state remains available from the ${formatAge(session.projection.snapshotAt)}-old snapshot.`;
  }

  return <div className="projection-banner" data-tone={tone} role="status">{copy}</div>;
}
